"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Alert, EmptyState, ListCount, ListSurface, ListSurfaceBody, ListSurfaceHeader, SkeletonTableRow } from "@/components/ui-system";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { TableHeaderSearch } from "@/components/ui-system/select";
import { getMasterViewSnapshot, listDevicesPage } from "@/lib/api/endpoints/devices";
import { useAuthStore } from "@/modules/auth/store";

const PAGE_SIZE_OPTIONS = [10, 20, 30];

function formatValue(value: number | boolean | string | null): string {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
  if (typeof value === "boolean") {
    return value ? "True" : "False";
  }
  return String(value);
}

function formatLocationContext(siteAssetContext: string | null, city: string | null): string | null {
  const parts = [siteAssetContext?.trim(), city?.trim()].filter((value): value is string => Boolean(value));
  if (parts.length === 0) {
    return null;
  }
  return parts.join(" / ");
}

export function DeviceMasterViewPage() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const role = useAuthStore((s) => s.user?.role ?? null);
  const accountType = useAuthStore((s) => s.user?.accountType ?? "customer");
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const impersonationStartedAt = useAuthStore((s) => s.impersonation?.started_at ?? null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const isSuperAdmin = role === "super_admin" || accountType === "platform";
  const workspaceScope = isSuperAdmin && !tenantId ? "all" : null;
  const effectiveTenantId = tenantId;

  useEffect(() => {
    setPage(0);
  }, [effectiveTenantId, workspaceScope]);

  const devicesQuery = useQuery({
    queryKey: ["master-view", userId ?? "anon", role ?? "unknown", effectiveTenantId ?? "none", workspaceScope ?? "tenant", impersonationStartedAt ?? "direct", search, page, pageSize],
    queryFn: () =>
      listDevicesPage({
        tenantId: effectiveTenantId,
        workspaceScope,
        search,
        limit: pageSize,
        offset: page * pageSize,
        moduleKey: "master_view",
      }),
    enabled: isSuperAdmin || Boolean(tenantId),
    staleTime: 15_000,
  });

  const deviceIds = useMemo(
    () => (devicesQuery.data?.items ?? []).map((device) => device.id),
    [devicesQuery.data?.items],
  );

  const snapshotQuery = useQuery({
    queryKey: ["master-view", "snapshot", role ?? "unknown", effectiveTenantId ?? "none", workspaceScope ?? "tenant", deviceIds.join(",")],
    queryFn: () => getMasterViewSnapshot(deviceIds),
    enabled: deviceIds.length > 0,
    staleTime: 10_000,
  });

  const rows = snapshotQuery.data?.items ?? [];
  const maxChannels = useMemo(
    () => rows.reduce((max, item) => Math.max(max, item.channels.length), 0),
    [rows],
  );
  const total = devicesQuery.data?.total ?? 0;
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 1;

  return (
    <EnterprisePageShell
      hideHeader
      kicker="Data loggers"
      title="Master View"
      description="Latest channel snapshot for the loggers available to your role in this workspace."
    >
      <ListSurface>
        <ListSurfaceHeader>
          <div className="min-w-0">
            <h2 className="text-title font-semibold text-foreground">Logger snapshot table</h2>
            <ListCount total={total} noun={total === 1 ? "logger" : "loggers"} />
          </div>
          <div className="flex w-full flex-wrap items-center justify-end gap-ds-2 sm:w-auto">
            <TableHeaderSearch
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(0);
              }}
              placeholder="Search logger name or UID"
              aria-label="Search master view"
              className="w-full sm:w-[18rem]"
            />
          </div>
        </ListSurfaceHeader>
        <ListSurfaceBody className="overflow-x-auto">
          {devicesQuery.isLoading ? (
            <div className="p-ds-4" aria-label="Loading loggers">
              {Array.from({ length: 5 }, (_, index) => <SkeletonTableRow key={index} />)}
            </div>
          ) : devicesQuery.isError ? (
            <Alert className="m-ds-4" tone="error" title="Master View unavailable" description={devicesQuery.error instanceof Error ? devicesQuery.error.message : "Could not load master view."} />
          ) : total === 0 ? (
            <EmptyState
              title="No loggers available"
              description="No loggers in this workspace currently grant Master View access for your account."
            />
          ) : snapshotQuery.isLoading ? (
            <div className="p-ds-4" aria-label="Loading channel snapshots">
              {Array.from({ length: Math.min(total, 5) }, (_, index) => <SkeletonTableRow key={index} />)}
            </div>
          ) : snapshotQuery.isError ? (
            <Alert className="m-ds-4" tone="error" title="Channel snapshots unavailable" description={snapshotQuery.error instanceof Error ? snapshotQuery.error.message : "Could not load channel snapshots."} />
          ) : (
            <table
              className="table-fixed border-collapse"
              style={{ width: `${16 + Math.max(1, maxChannels) * 14}rem` }}
            >
              <colgroup>
                <col className="w-[16rem]" />
                {Array.from({ length: maxChannels }, (_, index) => (
                  <col key={index} className="w-[14rem]" />
                ))}
              </colgroup>
              <thead>
                <tr className="bg-muted-bg/70 text-left text-micro uppercase tracking-[0.16em] text-muted">
                  <th className="w-[16rem] min-w-[16rem] px-ds-4 py-ds-3">Logger</th>
                  {Array.from({ length: maxChannels }, (_, index) => (
                    <th key={index} className="w-[14rem] max-w-[14rem] px-ds-4 py-ds-3 text-center">
                      C{index + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.device_id} className="border-t border-border/60 align-top">
                    <td className="w-[16rem] min-w-[16rem] px-ds-4 py-ds-4">
                      {(() => {
                        const locationContext = formatLocationContext(row.site_asset_context, row.city);
                        return (
                      <div className="space-y-1">
                        <Link
                          to={`/explorer?device=${row.device_id}`}
                          className="block font-semibold text-foreground underline-offset-4 hover:underline"
                        >
                          {row.name?.trim() || row.device_uid}
                        </Link>
                        {locationContext ? <div className="text-caption text-muted">{locationContext}</div> : null}
                        <div className="text-caption text-muted">
                          {row.activity_status ?? "UNKNOWN"}
                          {row.activity_delay_label ? ` - ${row.activity_delay_label}` : ""}
                        </div>
                      </div>
                        );
                      })()}
                    </td>
                    {row.channels.map((channel) => (
                      <td key={channel.id} className="w-[14rem] max-w-[14rem] px-ds-3 py-ds-3 text-center">
                        <div
                          className={`min-w-[11rem] rounded-control border px-ds-3 py-ds-4 text-left ${
                            channel.status === "breached"
                              ? "border-status-error-border bg-status-error-surface"
                              : channel.status === "ok"
                                ? "border-status-success-border bg-status-success-surface"
                                : "border-card-border bg-surface-secondary"
                          }`}
                        >
                          <div className="truncate text-micro font-semibold uppercase tracking-[0.12em] text-muted">
                            {channel.label}
                          </div>
                          <div
                            className={`mt-1 text-body font-semibold leading-tight ${
                              channel.status === "breached"
                                ? "text-error"
                                : channel.status === "ok"
                                  ? "text-success"
                                  : "text-foreground"
                            }`}
                          >
                            {formatValue(channel.value)}
                            {channel.unit ? <span className="ml-1 text-caption font-medium">{channel.unit}</span> : null}
                          </div>
                        </div>
                      </td>
                    ))}
                    {Array.from({ length: Math.max(0, maxChannels - row.channels.length) }, (_, index) => (
                      <td key={`${row.device_id}-empty-${index}`} className="px-ds-4 py-ds-4" />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ListSurfaceBody>
        {total > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-ds-3 border-t border-border/60 px-ds-4 py-ds-3 text-caption text-muted">
            <div className="flex flex-wrap items-center gap-ds-3">
              <span>Page {page + 1} of {totalPages}</span>
              <label className="flex items-center gap-ds-2">
                <span>Rows per page</span>
                <select
                  className="ui-control h-10 min-w-[5rem]"
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(0);
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-ds-2">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(0, current - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={(page + 1) * pageSize >= total}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </ListSurface>
    </EnterprisePageShell>
  );
}
